import React from "react";
import { Dropdown } from "primereact/dropdown";

/**
 * AppDropdown — consistent single-select dropdown for the whole application.
 *
 * Mirrors the style used in AdminSetupV2 tabs:
 *   height 40px, border #E9ECEF, white background, appendTo="self",
 *   scrollHeight="200px" (internal scroll — never pushes page layout).
 *
 * Props:
 *   value          - currently selected value
 *   options        - array of { label, value, description? } objects. A `description` is shown as a small
 *                    grey second line under the label in the list; the control itself shows only the label.
 *   onChange       - (e) => void  — e.value is the new value
 *   placeholder    - string
 *   disabled       - bool
 *   filter         - bool (enables search box inside panel)
 *   error          - bool (red border when true)
 *   style          - extra inline styles for the root element
 *   panelStyle     - extra inline styles for the dropdown panel
 *   scrollHeight   - override default "200px" ("400px" when options carry a description)
 *   className      - extra class names
 */
const renderOptionWithDescription = (option) => {
  if (!option) return null;
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{option.label}</div>
      {option.description ? <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>{option.description}</div> : null}
    </div>
  );
};

const AppDropdown = ({
  value,
  options = [],
  onChange,
  placeholder = "Select...",
  disabled = false,
  filter = false,
  error = false,
  style = {},
  panelStyle = {},
  scrollHeight,
  className = "",
  ...rest
}) => {
  const hasDescriptions = options.some((o) => o && o.description);
  if (scrollHeight === undefined) scrollHeight = hasDescriptions ? "400px" : "200px";
  return (
    <Dropdown
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      filter={filter}
      scrollHeight={scrollHeight}
      appendTo="self"
      itemTemplate={hasDescriptions ? renderOptionWithDescription : undefined}
      className={`w-100 ${className}`}
      style={{
        width: "100%",
        height: "40px",
        backgroundColor: disabled ? "#f8f9fa" : "#fff",
        border: error ? "1px solid #dc3545" : "1px solid #E9ECEF",
        borderRadius: "6px",
        fontSize: "14px",
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
      panelStyle={{
        maxHeight: scrollHeight,
        overflowY: "auto",
        ...panelStyle,
      }}
      {...rest}
    />
  );
};

export default AppDropdown;
